/**
 * User Management Page
 * Admin interface for managing users and their site assignments
 *
 * Per AERO-EDC spec:
 * - 4 roles: admin, site-rc, analyst, cec
 * - site-rc users have assignedSites
 * - Permissions are role-based, not per-user
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';

interface Site {
  _id: string;
  siteId: string;
  siteName: string;
}

interface User {
  _id: string;
  username: string;
  email: string;
  fullName: string;
  role: 'admin' | 'site-rc' | 'site' | 'analyst' | 'cec';
  organization?: string;
  isActive: boolean;
  assignedSites?: Site[];
  mustChangePassword?: boolean;
  lastLogin?: string;
  createdAt: string;
}

interface UserFormData {
  username: string;
  email: string;
  password: string;
  fullName: string;
  role: string;
  organization: string;
  isActive: boolean;
  assignedSites: string[];
}

const emptyUserForm: UserFormData = {
  username: '',
  email: '',
  password: '',
  fullName: '',
  role: 'site-rc',
  organization: '',
  isActive: true,
  assignedSites: []
};

const roleLabels: Record<string, string> = {
  admin: 'Administrator',
  'site-rc': 'Site Research Coordinator',
  site: 'Site User',
  analyst: 'Core Lab Analyst',
  cec: 'CEC Member'
};

const roleColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800',
  'site-rc': 'bg-blue-100 text-blue-800',
  site: 'bg-opci-navy-100 text-opci-navy-700',
  analyst: 'bg-green-100 text-green-800',
  cec: 'bg-amber-100 text-amber-800'
};

const roleDescriptions: Record<string, string> = {
  admin: 'Full system access. Can manage users, sites, and all data.',
  'site-rc': 'Can enter and edit site forms for assigned sites.',
  site: 'Can enter and edit site forms for assigned sites.',
  analyst: 'Can enter and edit corelab analysis forms.',
  cec: 'Can adjudicate adverse events and enter CEC forms.'
};

const UserManagement: React.FC = () => {
  const { token, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const isSiteRC = currentUser?.role === 'site-rc';
  const isAdmin = currentUser?.role === 'admin';
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>(emptyUserForm);
  const [error, setError] = useState<string | null>(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Fetch sites for assignment
  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      const res = await fetch('/api/sites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    }
  });

  // Fetch users
  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      return res.json();
    }
  });

  // Create/Update user mutation
  const saveMutation = useMutation({
    mutationFn: async (userData: any) => {
      const url = editingUser ? `/api/users/${editingUser._id}` : '/api/users';
      const method = editingUser ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || err.errors?.[0]?.msg || 'Failed to save user');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowModal(false);
      setEditingUser(null);
      setFormData(emptyUserForm);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
    }
  });

  // Delete (deactivate) user mutation
  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || 'Failed to delete user');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: Error) => {
      alert(err.message);
    }
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, password }: { userId: string; password: string }) => {
      const res = await fetch(`/api/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword: password })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || err.errors?.[0]?.msg || 'Failed to reset password');
      }
      return res.json();
    },
    onSuccess: () => {
      setShowResetPassword(false);
      setNewPassword('');
      setEditingUser(null);
      setError(null);
      alert('Password reset successfully. User must change password on next login.');
    },
    onError: (err: Error) => {
      setError(err.message);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const userData: any = {
      fullName: formData.fullName,
      email: formData.email,
      role: formData.role,
      organization: formData.organization,
      isActive: formData.isActive
    };

    // Only include password for new users
    if (!editingUser) {
      userData.username = formData.username;
      userData.password = formData.password;
    }

    // site-rc and site users have assignedSites
    if (formData.role === 'site-rc' || formData.role === 'site') {
      userData.assignedSites = formData.assignedSites;
    }

    saveMutation.mutate(userData);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      fullName: user.fullName,
      role: user.role,
      organization: user.organization || '',
      isActive: user.isActive,
      assignedSites: user.assignedSites?.map(s => s._id) || []
    });
    setShowModal(true);
    setError(null);
  };

  const handleAdd = () => {
    setEditingUser(null);
    if (isSiteRC) {
      // Site-RC: default to 'site' role with their assigned site pre-selected
      const mySiteIds = currentUser?.assignedSites?.map((s: any) => s._id) || [];
      setFormData({ ...emptyUserForm, role: 'site', assignedSites: mySiteIds });
    } else {
      setFormData(emptyUserForm);
    }
    setError(null);
    setShowModal(true);
  };

  const users: User[] = data?.users || [];
  const sites: Site[] = sitesData?.sites || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isSiteRC ? 'Site Users' : 'User Management'}
          </h1>
          <p className="text-gray-600">
            {isSiteRC ? 'Manage users for your site' : 'Manage users and their site access'}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add User
        </button>
      </div>

      {/* Role Legend - admin only */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Role Permissions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(roleLabels).map(([role, label]) => (
              <div key={role} className="text-sm">
                <span className={`px-2 py-1 text-xs rounded-full ${roleColors[role]}`}>
                  {label}
                </span>
                <p className="text-gray-500 mt-1 text-xs">{roleDescriptions[role]}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No users found.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Sites</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{user.fullName}</div>
                    <div className="text-sm text-gray-500">{user.username} • {user.email}</div>
                    {user.mustChangePassword && (
                      <span className="text-xs text-orange-600">Must change password</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${roleColors[user.role]}`}>
                      {roleLabels[user.role]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {user.role === 'admin' ? (
                      <span className="text-gray-400">All sites (admin)</span>
                    ) : (user.role === 'site-rc' || user.role === 'site') && user.assignedSites && user.assignedSites.length > 0 ? (
                      <span>{user.assignedSites.map(s => s.siteId).join(', ')}</span>
                    ) : (user.role === 'site-rc' || user.role === 'site') ? (
                      <span className="text-red-500">No sites assigned</span>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {user.isActive ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Inactive</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => handleEdit(user)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    {user._id !== currentUser?._id && user._id !== currentUser?.id && (
                      <>
                        <button
                          onClick={() => {
                            setEditingUser(user);
                            setShowResetPassword(true);
                            setNewPassword('');
                            setError(null);
                          }}
                          className="text-orange-600 hover:text-orange-800"
                        >
                          Reset PW
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => {
                              if (confirm(`Are you sure you want to deactivate "${user.fullName}"? They will no longer be able to log in.`)) {
                                deleteMutation.mutate(user._id);
                              }
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={!!editingUser}
                    minLength={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required={!editingUser}
                    minLength={8}
                    placeholder="Min 8 chars, uppercase, lowercase, number, special char"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Must contain: uppercase, lowercase, number, and special character (!@#$%^&amp;*etc)
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role *
                  </label>
                  {isSiteRC ? (
                    <>
                      <input
                        type="text"
                        value="Site User"
                        disabled
                        className="w-full px-3 py-2 border rounded-lg bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {roleDescriptions['site']}
                      </p>
                    </>
                  ) : (
                    <>
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="site-rc">Site Research Coordinator</option>
                        <option value="site">Site User</option>
                        <option value="analyst">Core Lab Analyst</option>
                        <option value="cec">CEC Member</option>
                        <option value="admin">Administrator</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {roleDescriptions[formData.role]}
                      </p>
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization
                  </label>
                  <input
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Site Assignments - For site-rc and site roles */}
              {(formData.role === 'site-rc' || formData.role === 'site') && (
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Site</label>
                  {isSiteRC ? (
                    <>
                      <input
                        type="text"
                        value={currentUser?.assignedSites?.map((s: any) => `${s.siteId} - ${s.siteName}`).join(', ') || 'Your site'}
                        disabled
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-gray-50"
                      />
                      <p className="text-xs text-green-600 mt-1">User will be assigned to your site</p>
                    </>
                  ) : sites.length > 0 ? (
                    <>
                      <select
                        value={formData.assignedSites[0] || ''}
                        onChange={(e) => setFormData({ ...formData, assignedSites: e.target.value ? [e.target.value] : [] })}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="">Select a site...</option>
                        {sites.map((site) => (
                          <option key={site._id} value={site._id}>
                            {site.siteId} - {site.siteName}
                          </option>
                        ))}
                      </select>
                      {formData.assignedSites.length === 0 && (
                        <p className="text-xs text-orange-600 mt-2">
                          Warning: Site users need an assigned site to enter data.
                        </p>
                      )}
                    </>
                  ) : null}
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="isActive" className="text-sm">Account is active</label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                    setError(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Reset Password</h2>
            <p className="text-gray-600 mb-4">
              Reset password for <strong>{editingUser.fullName}</strong>
            </p>
            <p className="text-sm text-orange-600 mb-4">
              User will be required to change password on next login.
            </p>

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg mb-4">{error}</div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                minLength={8}
                placeholder="Min 8 chars, upper, lower, number, special"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setEditingUser(null);
                  setNewPassword('');
                  setError(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (newPassword.length >= 8) {
                    resetPasswordMutation.mutate({ userId: editingUser._id, password: newPassword });
                  } else {
                    setError('Password must be at least 8 characters');
                  }
                }}
                disabled={resetPasswordMutation.isPending}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
